import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["dropzone"];
  static values = {
    multiple: { type: Boolean, default: false },
  };

  #dropzone;
  #dropzoneAbortController = new AbortController();

  // In order to know exactly when the dropzone is left, and thus deactivating it by removing its
  // `dragover` attribute, the dragged-over elements are saved in a set on `dragenter`. Every time
  // `dragleave` is called, the left element is removed from the set. When the set is empty, it
  // means the pointer left the dropzone.
  #dragoverElements = new Set();

  connect() {
    // When no dropzone is defined, use the document root element, i.e. the app shell, as the
    // dropzone.
    this.#dropzone = this.hasDropzoneTarget ? this.dropzoneTarget : document.documentElement;

    this.#connectDropzone();
  }

  disconnect() {
    this.#disconnectDropzone();
  }

  // Public API methods

  // Private methods

  #connectDropzone() {
    // Passing the AbortController's signal enables removing the listeners with a single `abort()`
    // call and without needing to keep a reference of the listener's callback function.
    // Ref: https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener
    const signal = this.#dropzoneAbortController.signal;

    this.#dropzone.addEventListener("dragenter", this.#activateDropzone.bind(this), { signal });
    this.#dropzone.addEventListener("dragleave", this.#deactivateDropzone.bind(this), { signal });
    this.#dropzone.addEventListener("dragend", this.#resetDropzone.bind(this), { signal });
    this.#dropzone.addEventListener("dragover", this.#enableDrop.bind(this), { signal });
    this.#dropzone.addEventListener("drop", this.#handleDrop.bind(this), { signal });
  }

  #disconnectDropzone() {
    this.#dropzoneAbortController.abort(); // removes all the event listeners
    this.#resetDropzone();
  }

  // This function might be called even when the dropzone is already active (i.e. it has the
  // `dragover` attribute).
  #activateDropzone(event) {
    // Register the hovered element, which might be the dropzone itself or a descendant.
    this.#dragoverElements.add(event.target);

    if (this.#dropzone.hasAttribute("dragover")) return;

    const draggingFile = Array.from(event.dataTransfer.items).some((item) => item.kind === "file");
    if (draggingFile) this.#addDragoverState(this.#dropzone);
  }

  // This callback function is attached to the `dragleave` event. As this event bubbles up, it is
  // going to be called every time a descendant of the dropzone is left, or when the dropzone
  // itself is left.
  #deactivateDropzone(event) {
    this.#dragoverElements.delete(event.target);

    // When there are no dragged-over elements left, it means the pointer left the dropzone.
    if (this.#dragoverElements.size === 0) this.#resetDropzone();
  }

  // The following `preventDefault` enables the dropzone to receive the drop event.
  // Ref: https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/dragover_event
  #enableDrop(event) {
    event.preventDefault();
  }

  #handleDrop(event) {
    event.preventDefault();

    const fileList = event.dataTransfer.files;

    if (fileList.length > 0) {
      const files = this.multipleValue ? Array.from(fileList) : fileList.item(0);
      this.dispatch("drop", { detail: { files } });
    }

    this.#resetDropzone();
  }

  #resetDropzone() {
    this.#dragoverElements.clear();
    this.#removeDragoverState(this.#dropzone);
  }

  #addDragoverState(target) {
    target.setAttribute("dragover", "");
  }

  #removeDragoverState(target) {
    target.removeAttribute("dragover");
  }
}
